import { useState } from "react";
import type { ReactNode } from "react";

import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogContent } from "../ui/dialog";

/**
 * Extracted from `MarketplaceStatusCard`'s `FinalizingEscapeCard` (plan 435
 * M2, mfh-hero-escape-cancel) so Home's hero can trigger the SAME day-3
 * escape confirm dialog + cancel API instead of forking a second one
 * (tech-spec Concepts "Hero state" / decision "finalizing_escape's secondary
 * CTA ... the existing MarketplaceStatusCard cancel confirm dialog").
 *
 * This component owns the dialog's open/canceling/soft-note state; the
 * caller supplies the trigger element via a render prop so each surface can
 * keep its own trigger copy/testid (`MarketplaceStatusCard`'s
 * "Cancel and keep browsing" link vs Home's "Cancel request" secondary CTA)
 * while the dialog body/confirm/soft-note testids stay identical everywhere.
 */
export function CancelConnectionDialog({
  connectionId,
  onCancel,
  trigger,
}: {
  connectionId: string;
  onCancel?: (connectionId: string) => Promise<void>;
  trigger: (open: () => void) => ReactNode;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  // Scenario 1123: soft note shown inside the dialog when onCancel rejects
  // (e.g. a 409 ErrCancelTooEarly race). Never a raw error string. Never
  // payment vocabulary. The buyer stays in a calm finalizing state.
  const [cancelSoftNote, setCancelSoftNote] = useState(false);

  const openDialog = () => {
    setCancelSoftNote(false);
    setConfirmOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!onCancel) return;
    setCanceling(true);
    setCancelSoftNote(false);
    try {
      await onCancel(connectionId);
      // Success → close dialog; parent state transitions away from this card.
      setConfirmOpen(false);
    } catch {
      // Rejection (e.g. 409 ErrCancelTooEarly): keep dialog open, show soft
      // note. Never surface a failure message or payment vocabulary — the
      // buyer stays in a calm finalizing-escape state, still connected.
      setCancelSoftNote(true);
    } finally {
      setCanceling(false);
    }
  };

  return (
    <>
      {trigger(openDialog)}

      {/* Escape confirm dialog — scenario 1122 copy */}
      <Dialog
        onOpenChange={(open) => {
          if (!open) setCancelSoftNote(false);
          setConfirmOpen(open);
        }}
        open={confirmOpen}
      >
        <DialogContent
          description={
            <span data-testid="escape-dialog-body">
              You'll be released immediately and your offers stay live. If we
              can't finalize, this will cancel automatically after 7 days.
            </span>
          }
          showClose
          title="Cancel your connection?"
        >
          <div className="mt-4 flex flex-col gap-2">
            {/* Scenario 1123: soft note on rejection — calm, no failure vocab */}
            {cancelSoftNote && (
              <p
                className="text-sm text-muted-foreground"
                data-testid="escape-cancel-soft-note"
              >
                Not available right now — you're still connected. Keep waiting
                and try again shortly.
              </p>
            )}
            <Button
              className="w-full"
              data-testid="escape-confirm-button"
              disabled={canceling}
              loading={canceling}
              onClick={() => void handleConfirmCancel()}
              variant="destructive"
            >
              {canceling ? "Canceling…" : "Yes, cancel and keep browsing"}
            </Button>
            <DialogClose asChild>
              <Button className="w-full" variant="outline">
                Keep waiting
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
